/**
 * react-hook-form bound field components. Each one renders label, control,
 * description and (server or zod) error consistently:
 *
 * ```tsx
 * <TextField control={form.control} name="email" label={t("…")} type="email" />
 * ```
 */
import { X } from "lucide-react"
import * as React from "react"
import {
  type Control,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox"
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

export interface BaseFieldProps<T extends FieldValues, N extends FieldPath<T>> {
  control: Control<T>
  name: N
  label?: React.ReactNode
  description?: React.ReactNode
  /** Shows a subtle "required" marker; validation itself lives in the zod schema. */
  required?: boolean
  disabled?: boolean
  className?: string
}

/** Layout used by every bound field; also usable for custom controls. */
export function FieldShell({
  id,
  label,
  labelAction,
  description,
  error,
  required,
  className,
  children,
}: {
  id: string
  label?: React.ReactNode
  /** Small control shown right after the label (kept outside the `<label>` element). */
  labelAction?: React.ReactNode
  description?: React.ReactNode
  error?: { message?: string }
  required?: boolean
  className?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const labelEl = label && (
    <FieldLabel htmlFor={id} className="text-[0.8125rem]">
      {label}
      {required && (
        <span className="text-muted-foreground font-normal" aria-hidden>
          *<span className="sr-only">{t("form.required")}</span>
        </span>
      )}
    </FieldLabel>
  )
  return (
    <Field data-invalid={!!error || undefined} className={cn("gap-1.5", className)}>
      {labelEl && labelAction ? (
        <div className="flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1">
          {labelEl}
          {labelAction}
        </div>
      ) : (
        labelEl
      )}
      {children}
      {description && !error && <FieldDescription className="text-xs">{description}</FieldDescription>}
      <FieldError className="text-xs" errors={error ? [error] : undefined} />
    </Field>
  )
}

function useBound<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N>,
): { id: string; field: ControllerRenderProps<T, N>; fieldState: ControllerFieldState } {
  const id = React.useId()
  const { field, fieldState } = useController({
    control: props.control,
    name: props.name,
    disabled: props.disabled,
  })
  return { id, field, fieldState }
}

export interface TextFieldProps<T extends FieldValues, N extends FieldPath<T>> extends BaseFieldProps<T, N> {
  type?: "text" | "email" | "password" | "url" | "search" | "tel"
  placeholder?: string
  autoComplete?: string
  autoFocus?: boolean
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"]
  /** Text/icon rendered inside the input on the left (e.g. "https://"). */
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  mono?: boolean
  readOnly?: boolean
  maxLength?: number
  /** See {@link FieldShell}. */
  labelAction?: React.ReactNode
}

export function TextField<T extends FieldValues, N extends FieldPath<T>>(props: TextFieldProps<T, N>) {
  const { id, field, fieldState } = useBound(props)
  const inputProps = {
    id,
    type: props.type ?? "text",
    placeholder: props.placeholder,
    autoComplete: props.autoComplete,
    autoFocus: props.autoFocus,
    inputMode: props.inputMode,
    readOnly: props.readOnly,
    maxLength: props.maxLength,
    "aria-invalid": fieldState.invalid || undefined,
    name: field.name,
    ref: field.ref,
    onBlur: field.onBlur,
    disabled: field.disabled,
    value: (field.value as string | null | undefined) ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value),
    className: cn(props.mono && "font-mono text-[0.8125rem]"),
  }
  return (
    <FieldShell
      id={id}
      label={props.label}
      labelAction={props.labelAction}
      description={props.description}
      error={fieldState.error}
      required={props.required}
      className={props.className}
    >
      {props.prefix || props.suffix ? (
        <InputGroup>
          {props.prefix && (
            <InputGroupAddon>
              <InputGroupText>{props.prefix}</InputGroupText>
            </InputGroupAddon>
          )}
          <InputGroupInput {...inputProps} />
          {props.suffix && (
            <InputGroupAddon align="inline-end">
              <InputGroupText>{props.suffix}</InputGroupText>
            </InputGroupAddon>
          )}
        </InputGroup>
      ) : (
        <Input {...inputProps} />
      )}
    </FieldShell>
  )
}

export function TextareaField<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N> & { placeholder?: string; rows?: number; mono?: boolean; maxLength?: number },
) {
  const { id, field, fieldState } = useBound(props)
  return (
    <FieldShell
      id={id}
      label={props.label}
      description={props.description}
      error={fieldState.error}
      required={props.required}
      className={props.className}
    >
      <Textarea
        id={id}
        {...field}
        value={(field.value as string | null | undefined) ?? ""}
        rows={props.rows}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        aria-invalid={fieldState.invalid || undefined}
        className={cn("min-h-20", props.mono && "font-mono text-[0.8125rem]")}
      />
    </FieldShell>
  )
}

export function NumberField<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N> & {
    min?: number
    max?: number
    step?: number
    /** Unit shown inside the input (e.g. "MB", "days"). */
    unit?: React.ReactNode
    placeholder?: string
    /** Empty input becomes `null` instead of a validation error. */
    nullable?: boolean
  },
) {
  const { id, field, fieldState } = useBound(props)
  const value = field.value as number | null | undefined
  const [text, setText] = React.useState(value == null ? "" : String(value))
  React.useEffect(() => {
    // keep local text in sync on external resets
    setText((prev) => (Number(prev) === value && prev !== "" ? prev : value == null ? "" : String(value)))
  }, [value])
  const onChange = (raw: string) => {
    setText(raw)
    if (raw.trim() === "") field.onChange(props.nullable ? null : Number.NaN)
    else field.onChange(Number(raw))
  }
  const input = (
    <InputGroupInput
      id={id}
      type="number"
      inputMode="decimal"
      name={field.name}
      ref={field.ref}
      onBlur={field.onBlur}
      disabled={field.disabled}
      min={props.min}
      max={props.max}
      step={props.step}
      placeholder={props.placeholder}
      value={text}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={fieldState.invalid || undefined}
      className="tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  )
  return (
    <FieldShell
      id={id}
      label={props.label}
      description={props.description}
      error={fieldState.error}
      required={props.required}
      className={props.className}
    >
      <InputGroup>
        {input}
        {props.unit && (
          <InputGroupAddon align="inline-end">
            <InputGroupText className="text-xs">{props.unit}</InputGroupText>
          </InputGroupAddon>
        )}
      </InputGroup>
    </FieldShell>
  )
}

/**
 * Boolean toggle. `variant="card"` renders a bordered, fully clickable row
 * (good for permission lists).
 */
export function SwitchField<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N> & {
    variant?: "inline" | "card"
    icon?: React.ComponentType<{ className?: string }>
  },
) {
  const { id, field, fieldState } = useBound(props)
  const Icon = props.icon
  return (
    <Field
      orientation="horizontal"
      data-invalid={fieldState.invalid || undefined}
      data-disabled={field.disabled || undefined}
      className={cn(
        "items-start justify-between gap-4",
        props.variant === "card" &&
          "has-data-checked:border-primary/30 has-data-checked:bg-primary/[0.03] rounded-lg border p-3 transition-colors",
        props.className,
      )}
    >
      <FieldContent className="min-w-0">
        <FieldLabel htmlFor={id} className="text-[0.8125rem]">
          {Icon && <Icon className="text-muted-foreground size-4" />}
          {props.label}
        </FieldLabel>
        {props.description && <FieldDescription className="text-xs">{props.description}</FieldDescription>}
        <FieldError className="text-xs" errors={fieldState.error ? [fieldState.error] : undefined} />
      </FieldContent>
      <Switch
        id={id}
        name={field.name}
        inputRef={field.ref}
        checked={!!field.value}
        onCheckedChange={(v) => field.onChange(v)}
        onBlur={field.onBlur}
        disabled={field.disabled}
        className="mt-0.5"
      />
    </Field>
  )
}

export interface Option<V extends string | number = string> {
  value: V
  label: string
  description?: string
  disabled?: boolean
}

export function SelectField<
  T extends FieldValues,
  N extends FieldPath<T>,
  V extends string | number = string,
>(
  props: BaseFieldProps<T, N> & {
    options: Option<V>[]
    placeholder?: string
    /** Adds a "None" option mapping to `null`. */
    nullable?: boolean
    noneLabel?: string
  },
) {
  const { t } = useTranslation()
  const { id, field, fieldState } = useBound(props)
  const NONE = "__none__"
  const items = [
    ...(props.nullable ? [{ value: NONE, label: props.noneLabel ?? t("form.none") }] : []),
    ...props.options.map((o) => ({ value: String(o.value), label: o.label })),
  ]
  const current = field.value == null ? (props.nullable ? NONE : null) : String(field.value)
  return (
    <FieldShell
      id={id}
      label={props.label}
      description={props.description}
      error={fieldState.error}
      required={props.required}
      className={props.className}
    >
      <Select
        items={items}
        value={current}
        disabled={field.disabled}
        onValueChange={(v) => {
          if (v == null || v === NONE) return field.onChange(null)
          const opt = props.options.find((o) => String(o.value) === v)
          field.onChange(opt ? opt.value : v)
        }}
      >
        <SelectTrigger
          id={id}
          ref={field.ref}
          onBlur={field.onBlur}
          className="w-full"
          aria-invalid={fieldState.invalid || undefined}
        >
          <SelectValue placeholder={props.placeholder ?? t("form.selectPlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          {props.nullable && (
            <SelectItem value={NONE}>
              <span className="text-muted-foreground">{props.noneLabel ?? t("form.none")}</span>
            </SelectItem>
          )}
          {props.options.map((o) => (
            <SelectItem key={String(o.value)} value={String(o.value)} disabled={o.disabled}>
              <span className="flex flex-col">
                <span>{o.label}</span>
                {o.description && <span className="text-muted-foreground text-xs">{o.description}</span>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  )
}

/** Searchable single select for long option lists (timezones, models…). */
export function ComboboxField<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N> & { options: Option[]; placeholder?: string; emptyText?: string },
) {
  const { t } = useTranslation()
  const { id, field, fieldState } = useBound(props)
  const selected = props.options.find((o) => o.value === field.value) ?? null
  return (
    <FieldShell
      id={id}
      label={props.label}
      description={props.description}
      error={fieldState.error}
      required={props.required}
      className={props.className}
    >
      <Combobox
        items={props.options}
        value={selected}
        onValueChange={(o: Option | null) => field.onChange(o ? o.value : null)}
        disabled={field.disabled}
      >
        <ComboboxInput
          id={id}
          placeholder={props.placeholder ?? t("form.searchPlaceholder")}
          onBlur={field.onBlur}
          aria-invalid={fieldState.invalid || undefined}
          className="w-full"
        />
        <ComboboxContent>
          <ComboboxEmpty>{props.emptyText ?? t("form.noMatches")}</ComboboxEmpty>
          <ComboboxList>
            {(o: Option) => (
              <ComboboxItem key={o.value} value={o}>
                <span className="flex flex-col">
                  <span>{o.label}</span>
                  {o.description && <span className="text-muted-foreground text-xs">{o.description}</span>}
                </span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </FieldShell>
  )
}

/** Normalise a raw tag (trim, lowercase, strip leading dots). */
export function normalizeTag(raw: string, lowercase = true): string {
  const s = raw.trim().replace(/^\.+/, "")
  return lowercase ? s.toLowerCase() : s
}

/**
 * Free-form list of short strings (file extensions, scopes, labels).
 * Enter / comma / Tab adds, Backspace on empty removes the last tag, paste
 * splits on commas and whitespace.
 */
export function TagInput({
  id,
  value,
  onChange,
  placeholder,
  invalid,
  disabled,
  lowercase = true,
  validate,
  onBlur,
}: {
  id?: string
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  invalid?: boolean
  disabled?: boolean
  lowercase?: boolean
  /** Return false to reject a tag. */
  validate?: (tag: string) => boolean
  onBlur?: () => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = React.useState("")
  const inputRef = React.useRef<HTMLInputElement>(null)

  const add = (raw: string) => {
    const tags = raw
      .split(/[\s,;]+/)
      .map((s) => normalizeTag(s, lowercase))
      .filter(Boolean)
      .filter((s) => (validate ? validate(s) : true))
    if (!tags.length) return
    const next = [...value]
    for (const tag of tags) if (!next.includes(tag)) next.push(tag)
    onChange(next)
    setDraft("")
  }

  return (
    <div
      role="group"
      data-slot="tag-input"
      aria-invalid={invalid || undefined}
      onClick={() => inputRef.current?.focus()}
      className={cn(
        "border-input dark:bg-input/30 focus-within:border-ring focus-within:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex min-h-8 w-full cursor-text flex-wrap items-center gap-1 rounded-lg border bg-transparent px-1.5 py-1 text-sm transition-colors focus-within:ring-3",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {value.map((tag) => (
        <Badge key={tag} variant="secondary" className="h-6 gap-0.5 rounded-md pr-0.5 pl-2 font-mono text-xs">
          {tag}
          <button
            type="button"
            className="hover:bg-foreground/10 text-muted-foreground hover:text-foreground rounded p-0.5"
            onClick={(e) => {
              e.stopPropagation()
              onChange(value.filter((v) => v !== tag))
            }}
            aria-label={t("form.removeTag", { tag })}
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <input
        id={id}
        ref={inputRef}
        value={draft}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "," || (e.key === "Tab" && draft)) {
            if (draft.trim()) {
              e.preventDefault()
              add(draft)
            } else if (e.key === "Enter") e.preventDefault()
          } else if (e.key === "Backspace" && !draft && value.length) {
            onChange(value.slice(0, -1))
          }
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData("text")
          if (/[\s,;]/.test(text)) {
            e.preventDefault()
            add(text)
          }
        }}
        onBlur={() => {
          if (draft.trim()) add(draft)
          onBlur?.()
        }}
        className="placeholder:text-muted-foreground h-6 min-w-24 flex-1 bg-transparent px-1 outline-none"
      />
    </div>
  )
}

export function TagInputField<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N> & {
    placeholder?: string
    lowercase?: boolean
    validate?: (tag: string) => boolean
  },
) {
  const { id, field, fieldState } = useBound(props)
  return (
    <FieldShell
      id={id}
      label={props.label}
      description={props.description}
      error={fieldState.error}
      required={props.required}
      className={props.className}
    >
      <TagInput
        id={id}
        value={(field.value as string[] | undefined) ?? []}
        onChange={field.onChange}
        onBlur={field.onBlur}
        placeholder={props.placeholder}
        invalid={fieldState.invalid}
        disabled={field.disabled}
        lowercase={props.lowercase}
        validate={props.validate}
      />
    </FieldShell>
  )
}
